import {
  ProjectCockpitViewSchema,
  type ScheduleEntry,
  type ProjectCockpitApartmentTypeSummary,
  type ProjectCockpitInputRow,
  type ProjectCockpitScopeRow,
  type ProjectCockpitView,
  type RequirementTags,
  type RiskTags,
  type ScopeTags,
  type SourceRef
} from "@automatry/schemas";

import { labelFromId, projectLabelFromId } from "../app/format";
import type { ApartmentSolutionForDisplay } from "./solutionViewAdapter";

type FixtureInputStatus = "accepted" | "review" | "missing" | "not_produced";
type ScopeStatus = "included" | "by_others" | "excluded";
type ViewStatus = "loading" | "ready" | "blocked" | "error";

interface ProjectFixtureInputRow {
  id: string;
  kind: string;
  name: string;
  meta: string;
  status: FixtureInputStatus;
  sourceRefCount: number;
}

type ProjectFixtureRequirementStatus =
  | "candidate"
  | "approved"
  | "rejected"
  | "superseded";

interface ProjectFixtureRequirementRow {
  id: string;
  type: string;
  status: ProjectFixtureRequirementStatus;
  interpretedText: string;
  priority?: "blocking" | "default" | "option" | "ve" | "rfi";
  tags?: NonNullable<RequirementTags>;
  productQuantity?: unknown;
  confidence: number;
  sourceRefs?: SourceRef[];
}

interface ProjectFixtureScopeRow {
  id: string;
  item: string;
  status: ScopeStatus;
  tags?: NonNullable<ScopeTags>;
  rationale: string;
  sourceRefs?: SourceRef[];
  sourceRefCount?: number;
  ruleTraceRefs?: string[];
  ruleTraceRefCount?: number;
}

interface ProjectFixtureWarning {
  severity: "info" | "warning" | "blocker";
}

interface ProjectFixtureRisk {
  id: string;
  type?: string;
  severity?: "info" | "warning" | "blocker";
  title?: string;
  tags?: NonNullable<RiskTags>;
  status: string;
  blocksExport: boolean;
}

interface ProjectFixtureOpenDecision {
  id: string;
  stage: "lane-a" | "lane-b" | "output";
  subjectRef?: string;
  message?: string;
}

export interface ProjectFixtureNoteRow {
  id: string;
  projectId?: string;
  kind: "clarification" | "exclusion" | "commissioning" | "requirement-amendment";
  noteKind?: "clarification" | "exclusion" | "commissioning" | "requirement-amendment";
  text: string;
  authorType?: "user" | "ai";
  status?: "active" | "conflict_flagged" | "promoted_to_requirement";
  subjectRef?: {
    id: string;
    label: string;
    kind?: string;
  };
  aiProvenance?: {
    why: string;
    where: string;
    toolName?: string;
    model?: string;
    sourceMessageId?: string;
  };
  conflictsWith?: readonly string[];
  promotedRequirementId?: string;
  lastEditedBy?: "user" | "ai";
  createdAt: string;
  updatedAt?: string;
}

interface ProjectFixtureTakeoffRow {
  apartmentTypeId: string;
  rawLabel: string;
  quantity: number;
  beds?: number;
  fcuCount?: number;
  towelRailCount?: number;
  wufhZoneCount?: number;
  eufhZoneCount?: number;
  standaloneHeatOnlyZoneCount?: number;
  thermostatUiPointCount?: number;
  centralControlScreenCount?: number;
  hiuCount?: number;
  ciuCount?: number;
  althermaCount?: number;
}

export interface ProjectFixtureCompareMatrixOverride {
  groupId: "takeoffs" | "controls";
  kind: string;
  apartmentTypeId: string;
  value: number;
  reason?: string;
  updatedAt?: string;
  updatedBy?: "user";
}

export type ProjectFixtureOutputSupplySource =
  | "titan-supplied"
  | "fabricated"
  | "in-house"
  | "third-party";

export interface ProjectFixtureControlsBomRow {
  id: string;
  apartmentTypeId: string;
  itemClass: "controller" | "device" | "infrastructure";
  itemKind: string;
  productPartNo?: string;
  description: string;
  supplySource: ProjectFixtureOutputSupplySource;
  provisionScope?: "apartment_type" | "project";
  installIncluded?: boolean;
  provisionScopeBasis?: string;
  installIncludedBasis?: string;
  quantityPerApartmentType: number;
  apartmentTypeQuantity: number;
  projectQuantity: number;
  why: string;
  how: string;
  installNotes: string[];
  sourceRefs: SourceRef[];
  ruleTraceRefs: string[];
  riskRefs: string[];
  specItemRefs: string[];
}

export interface ProjectFixtureExportArtifact {
  id: string;
  exportBundleId: string;
  kind:
    | "specification"
    | "bom"
    | "risk_register"
    | "rfi_register"
    | "mapping_schedule"
    | "compliance_matrix"
    | "traceability_matrix"
    | "ve_summary";
  format: "json" | "xlsx" | "pdf" | "csv" | "md" | "html";
  storageRef: {
    bucket: string;
    path: string;
    hash: string;
    contentType: string;
  };
  hash: string;
  createdAt: string;
}

export interface ProjectFixtureExportBundle {
  id: string;
  orgId: string;
  projectId: string;
  specSnapshotId: string;
  outputGraphId: string;
  status: "draft" | "ready" | "blocked" | "superseded";
  artifacts: ProjectFixtureExportArtifact[];
  riskRefs: string[];
  createdAt: string;
}

export interface ProjectFixtureOutputDeviceInstance {
  id: string;
  specItemRef: string;
  productPartNo: string;
  label: string;
  quantity: number;
  apartmentTypeId?: string;
  metadata: Record<string, unknown>;
  sourceRefs: SourceRef[];
  ruleTraceRefs: string[];
}

export interface ProjectFixtureMappingScheduleRow {
  id: string;
  apartmentTypeId?: string;
  deviceInstanceId: string;
  logicalControllerRef?: string;
  physicalTerminationRef?: string;
  room?: string;
  cableImplication?: string;
  commissioningNote: string;
  riskRefs: string[];
  sourceRefs: SourceRef[];
  ruleTraceRefs: string[];
}

export interface ProjectFixtureCommissioningTask {
  id: string;
  projectId: string;
  title: string;
  description: string;
  deviceInstanceIds: string[];
  status: "planned" | "ready" | "blocked" | "done";
  riskRefs: string[];
  sourceRefs: SourceRef[];
  ruleTraceRefs: string[];
}

export interface ProjectFixtureOutputGraph {
  id: string;
  orgId: string;
  projectId: string;
  version: string;
  specSnapshotId: string;
  status: "draft" | "ready" | "blocked" | "superseded";
  deviceInstances: ProjectFixtureOutputDeviceInstance[];
  mappingScheduleRows: ProjectFixtureMappingScheduleRow[];
  commissioningTasks: ProjectFixtureCommissioningTask[];
  exportBundleIds: string[];
  productQuantityTotals: Record<string, number>;
  createdAt: string;
}

export interface ProjectFixtureOutputBundle {
  id: string;
  status: "ready" | "blocked";
  hash: string;
  exportBundles: ProjectFixtureExportBundle[];
  bomRows: ProjectFixtureControlsBomRow[];
  scopeMatrixRows: ProjectFixtureScopeRow[];
  complianceRows: unknown[];
  outputGraphs: ProjectFixtureOutputGraph[];
}

export interface ProjectFixtureQuotePricingContext {
  currency: "GBP";
  catalogReleaseId: string;
  catalogVersion: string;
  priceListName: string;
  priceListVersion: string;
  priceListDate: string;
  sourceRefs: SourceRef[];
}

export interface ProjectFixtureQuoteCatalogProduct {
  partNo: string;
  aliases: string[];
  description: string;
  family?: string;
  sourceRefs: SourceRef[];
  capabilities?: {
    primaryApplication?: string;
    roles?: string[];
    notes?: string[];
  };
  metadata?: {
    commercialModel?: string;
    edgeRole?: string;
  };
  price?: {
    amount: number;
    currency: "GBP" | "EUR" | "USD";
    sourceRefs: SourceRef[];
  };
}

export interface ProjectFixtureQuotePricing {
  generatedAt: string;
  pricingContext: ProjectFixtureQuotePricingContext;
  catalogProducts: ProjectFixtureQuoteCatalogProduct[];
}

export interface ProjectFixtureDraftScopeType {
  apartmentTypeId: string;
  draftProvenance: string;
  unresolvedFindings: {
    code: string;
    severity: string;
    message: string;
    details?: Record<string, unknown>;
  }[];
}

export interface ProjectFixtureDraftScope {
  status: "draft";
  certificationStatus: "not_certified";
  message: string;
  verifiedApartmentTypeIds: string[];
  includedDraftApartmentTypeIds: string[];
  types: ProjectFixtureDraftScopeType[];
}

export interface ProjectFixtureForDisplay {
  fixture: {
    generatedAt: string;
    generatedFrom: string;
    pipelineInputMode: string;
    sourceInput: string;
    advisories?: {
      types: {
        apartmentTypeId: string;
        terminalFindings: {
          code: string;
          severity: string;
          message: string;
          details?: Record<string, unknown>;
          machineSummary?: unknown;
          verifierResultId?: string;
          checkKind?: string;
        }[];
      }[];
    };
    partialScope?: {
      status: "partial";
      message: string;
      completedApartmentTypeIds: string[];
      failedApartmentTypeIds: string[];
      stoppedApartmentTypeIds: string[];
      omittedApartmentTypeIds: string[];
      failureKinds: string[];
    };
    /**
     * Track 1: present only when at least one apartment type is a DRAFT —
     * usable, materialized, and explicitly not certified. Absent entirely for
     * a fully certified run, so certified workspaces are unchanged.
     */
    draftScope?: ProjectFixtureDraftScope;
    compareMatrixOverrides?: ProjectFixtureCompareMatrixOverride[];
  };
  project: {
    projectId: string;
    name?: string;
    label?: string;
    activeCatalogReleaseId: string;
    activeRuleSetId: string;
    activeSpecSnapshotId?: string;
    requirementsFingerprint?: string;
    outputBundle: {
      status: "ready" | "blocked";
    };
    requirementIds: string[];
    approvedRequirementIds: string[];
    requirements: ProjectFixtureRequirementRow[];
  };
  outputBundle: ProjectFixtureOutputBundle;
  quotePricing?: ProjectFixtureQuotePricing;
  inputs: ProjectFixtureInputRow[];
  takeoffRows: ProjectFixtureTakeoffRow[];
  scopeMatrixRows: ProjectFixtureScopeRow[];
  warnings: ProjectFixtureWarning[];
  risks: ProjectFixtureRisk[];
  openDecisions: ProjectFixtureOpenDecision[];
  apartmentSolutions: ApartmentSolutionForDisplay[];
  scheduleEntries?: ScheduleEntry[];
  projectNotes?: ProjectFixtureNoteRow[];
}

export interface ProjectCockpitAttentionCard {
  id: "decisions" | "risks" | "assumptions" | "clarifications";
  label: string;
  count: number;
  hint: string;
  accent: string;
  target: "decisions" | "risks" | "requirements";
}

export interface ProjectCockpitStep {
  id: "inputs" | "spec" | "risks" | "scope" | "export";
  label: string;
  stateLabel: string;
  state: "done" | "active" | "todo" | "blocked";
}

export interface ProjectCockpitApartmentTypeDisplaySummary extends ProjectCockpitApartmentTypeSummary {
  hasExplicitEdgeFeature: boolean;
  edgeDefaultApplied: boolean;
  edgeInclusionWhy: string;
  edgeInclusionWhere: string;
  edgeCapabilities: string[];
}

export interface ProjectCockpitDisplayViewModel extends ProjectCockpitView {
  projectLabel: string;
  title: string;
  description: string;
  sourceInput: string;
  pipelineInputMode: string;
  partialScope: ProjectFixtureForDisplay["fixture"]["partialScope"] | null;
  approvedRequirementCount: number;
  openDecisionCount: number;
  needsUserDecisionCount: number;
  clientClarificationCount: number;
  genuineRiskCount: number;
  assumptionCount: number;
  clarificationCount: number;
  warningCount: number;
  mappingRowCount: number;
  scopeRows: ProjectCockpitScopeRow[];
  inputRows: ProjectCockpitInputRow[];
  apartmentTypes: ProjectCockpitApartmentTypeDisplaySummary[];
  dataGaps: string[];
  scopeCounts: {
    included: number;
    byOthers: number;
    excluded: number;
  };
  attention: ProjectCockpitAttentionCard[];
  steps: ProjectCockpitStep[];
}

const labelise = (value: string, projectId?: string): string =>
  labelFromId(value.replaceAll("_", "-"), projectId);

const carringtonProjectTitle = "One Carrington / 21 Carrington Street Apartments";

const projectTitleFromFixture = (fixture: ProjectFixtureForDisplay): string => {
  const explicitTitle = fixture.project.name ?? fixture.project.label;

  if (explicitTitle?.trim()) {
    return explicitTitle.trim();
  }

  if (fixture.project.projectId === "carrington") {
    return carringtonProjectTitle;
  }

  const inputTitle = fixture.inputs[0]?.name.replace(/\s+take-?off$/i, "").trim();

  return inputTitle || projectLabelFromId(fixture.project.projectId);
};

const countAssumptions = (solutions: readonly ApartmentSolutionForDisplay[]): number =>
  solutions.reduce(
    (sum, solution) =>
      sum +
      solution.justifications.reduce(
        (inner, justification) => inner + justification.assumptions.length,
        0
      ),
    0
  );

const countOpenRisks = (risks: readonly ProjectFixtureRisk[]): number =>
  risks.filter((risk) => risk.status === "open" && risk.type !== "needsUserDecision")
    .length;

const countNeedsUserDecisions = (fixture: ProjectFixtureForDisplay): number =>
  fixture.risks.filter(
    (risk) => risk.status === "open" && risk.type === "needsUserDecision"
  ).length;

const countClientClarifications = (fixture: ProjectFixtureForDisplay): number => {
  const needsUserDecisionRiskIds = new Set(
    fixture.risks
      .filter((risk) => risk.type === "needsUserDecision")
      .map((risk) => risk.id)
  );

  return fixture.openDecisions.filter(
    (decision) =>
      decision.stage !== "lane-b" ||
      !decision.subjectRef ||
      !needsUserDecisionRiskIds.has(decision.subjectRef)
  ).length;
};

const countFcuZones = (solution: ApartmentSolutionForDisplay): number =>
  solution.logicalZones.filter((zone) => zone.zoneKind === "fcu").length;

const noTriggerDefaultLabel =
  "Default - no specific requirement found in the documents";

const defaultEdgeCapabilities = [
  "Edge gateway allowance",
  "BACnet/IP dashboard path",
  "Alert-ready workflow"
];

const hasExplicitEdgeOutput = (solution: ApartmentSolutionForDisplay): boolean =>
  (solution.edgeFeatures ?? []).length > 0 ||
  solution.infrastructure.some((item) =>
    ["cloud_service", "edge_compute", "edge_enclosure", "network_switch"].includes(
      item.itemKind
    )
  );

const edgeInclusionForSolution = (
  solution: ApartmentSolutionForDisplay
): Pick<
  ProjectCockpitApartmentTypeDisplaySummary,
  | "hasEdgeFeature"
  | "hasExplicitEdgeFeature"
  | "edgeDefaultApplied"
  | "edgeInclusionWhy"
  | "edgeInclusionWhere"
  | "edgeCapabilities"
> => {
  const hasExplicitEdgeFeature = hasExplicitEdgeOutput(solution);

  return {
    hasEdgeFeature: true,
    hasExplicitEdgeFeature,
    edgeDefaultApplied: !hasExplicitEdgeFeature,
    edgeInclusionWhy: hasExplicitEdgeFeature
      ? "AI-produced Edge output is attached to this apartment type."
      : `${noTriggerDefaultLabel}. Edge is included for every controls project unless removed during review.`,
    edgeInclusionWhere: hasExplicitEdgeFeature
      ? "Where: project overview summary reports Edge output is present on this apartment type."
      : "Where: no project-specific Edge trigger is attached to this overview card.",
    edgeCapabilities: defaultEdgeCapabilities
  };
};

const displayScopeStatus = (status: ScopeStatus) =>
  status === "by_others" ? "byOthers" : status;

const scopeRows = (fixture: ProjectFixtureForDisplay): ProjectCockpitScopeRow[] =>
  fixture.scopeMatrixRows.map((row) => {
    const sourceRefs = row.sourceRefs ?? [];
    const ruleTraceRefs = row.ruleTraceRefs ?? [];
    const sourceRefCount = row.sourceRefCount ?? sourceRefs.length;
    const ruleTraceRefCount = row.ruleTraceRefCount ?? ruleTraceRefs.length;
    const firstSourceRef = sourceRefs[0];

    return {
      id: row.id,
      title: row.item,
      status: row.status,
      ...(row.tags ? { tags: row.tags } : {}),
      rationale: row.rationale,
      label: row.item,
      displayStatus: displayScopeStatus(row.status),
      sourceLinkStatus: firstSourceRef ? "available" : "not_produced",
      sourceLinkLabel: firstSourceRef?.locator ?? "Generated rationale",
      sourceRefs,
      sourceRefCount,
      ruleTraceRefs,
      ruleTraceRefCount
    };
  });

const scopeCounts = (rows: readonly ProjectCockpitScopeRow[]) => ({
  included: rows.filter((row) => row.status === "included").length,
  byOthers: rows.filter((row) => row.status === "by_others").length,
  excluded: rows.filter((row) => row.status === "excluded").length
});

const apartmentTypes = (
  fixture: ProjectFixtureForDisplay
): ProjectCockpitApartmentTypeDisplaySummary[] => {
  const takeoffByType = new Map(
    fixture.takeoffRows.map((row) => [row.apartmentTypeId, row])
  );

  return fixture.apartmentSolutions.map((solution) => {
    const takeoff = takeoffByType.get(solution.apartmentTypeId);

    return {
      apartmentTypeId: solution.apartmentTypeId,
      label: labelise(solution.apartmentTypeId, fixture.project.projectId),
      quantity: solution.quantity,
      ...(takeoff?.beds !== undefined ? { beds: takeoff.beds } : {}),
      controllerCount: solution.controllers.length,
      deviceCount:
        solution.devices.length +
        solution.infrastructure.length +
        (solution.edgeFeatures ?? []).length,
      fcuCount: takeoff?.fcuCount ?? countFcuZones(solution),
      hasOpenRisk: solution.risks.some(
        (risk) => risk.status === "open" && risk.type !== "needsUserDecision"
      ),
      hasOpenDecision: solution.risks.some(
        (risk) => risk.status === "open" && risk.type === "needsUserDecision"
      ),
      ...edgeInclusionForSolution(solution)
    };
  });
};

const apartmentTypesForSchema = (
  rows: readonly ProjectCockpitApartmentTypeDisplaySummary[]
): ProjectCockpitApartmentTypeSummary[] =>
  rows.map((row) => ({
    apartmentTypeId: row.apartmentTypeId,
    label: row.label,
    quantity: row.quantity,
    ...(row.beds !== undefined ? { beds: row.beds } : {}),
    controllerCount: row.controllerCount,
    deviceCount: row.deviceCount,
    fcuCount: row.fcuCount,
    hasOpenRisk: row.hasOpenRisk,
    ...(row.hasOpenDecision !== undefined
      ? { hasOpenDecision: row.hasOpenDecision }
      : {}),
    hasEdgeFeature: row.hasEdgeFeature
  }));

const exportReadiness = (
  status: ProjectFixtureForDisplay["project"]["outputBundle"]["status"]
): ProjectCockpitView["exportReadiness"] => (status === "ready" ? "ready" : "blocked");

const cockpitStatus = (
  status: ProjectFixtureForDisplay["project"]["outputBundle"]["status"]
): ViewStatus => (status === "ready" ? "ready" : "blocked");

const buildAttention = (counts: {
  openRiskCount: number;
  needsUserDecisionCount: number;
  assumptionCount: number;
  clarificationCount: number;
}): ProjectCockpitAttentionCard[] => [
  {
    id: "decisions",
    label: "Choices to confirm",
    count: counts.needsUserDecisionCount,
    hint: "Product or scope choices that need a person to confirm before the specification is final.",
    // C4-W2 fix-2: these render via inline style (borderLeftColor/color),
    // so a fixed literal never picked up the earlier light-theme class-based
    // remap. var(--warn)/var(--risk)/var(--network) keep them AA in dark
    // theme (index.css) with no light-theme change.
    accent: "var(--warn)",
    target: "decisions"
  },
  {
    id: "risks",
    label: "Open risks",
    count: counts.openRiskCount,
    hint: "Known design or capacity risks that could affect the specification.",
    accent: "var(--risk)",
    target: "risks"
  },
  {
    id: "assumptions",
    label: "Assumptions",
    count: counts.assumptionCount,
    hint: "Places where the AI made a reasonable assumption because the source documents were not explicit.",
    accent: "var(--network)",
    target: "requirements"
  },
  {
    id: "clarifications",
    label: "Clarifications",
    count: counts.clarificationCount,
    hint: "Open questions that should become client-ready requests for information.",
    accent: "var(--warn)",
    target: "risks"
  }
];

const buildSteps = (
  viewStatus: ViewStatus,
  approvedRequirementCount: number,
  needsUserDecisionCount: number,
  openRiskCount: number,
  mappingRowCount: number
): ProjectCockpitStep[] => [
  {
    id: "inputs",
    label: "Inputs",
    state: "done",
    stateLabel: `${approvedRequirementCount} requirement${
      approvedRequirementCount === 1 ? "" : "s"
    }`
  },
  {
    id: "spec",
    label: "Spec",
    state: "done",
    stateLabel: "Generated"
  },
  {
    id: "risks",
    label: "Risks",
    state: needsUserDecisionCount > 0 || openRiskCount > 0 ? "active" : "done",
    stateLabel:
      needsUserDecisionCount > 0
        ? `${needsUserDecisionCount} decision${needsUserDecisionCount === 1 ? "" : "s"}`
        : openRiskCount > 0
          ? `${openRiskCount} risk${openRiskCount === 1 ? "" : "s"}`
          : "Clear"
  },
  {
    id: "scope",
    label: "Scope",
    state: "done",
    stateLabel: "Drafted"
  },
  {
    id: "export",
    label: "Export",
    state: viewStatus === "blocked" ? "blocked" : "done",
    stateLabel:
      viewStatus === "blocked"
        ? "Blocked"
        : `${mappingRowCount} mapping row${mappingRowCount === 1 ? "" : "s"}`
  }
];

export const projectFixtureToCockpitView = (
  fixture: ProjectFixtureForDisplay
): ProjectCockpitDisplayViewModel => {
  const rows = scopeRows(fixture);
  const counts = scopeCounts(rows);
  const openRiskCount = countOpenRisks(fixture.risks);
  const needsUserDecisionCount = countNeedsUserDecisions(fixture);
  const assumptionCount = countAssumptions(fixture.apartmentSolutions);
  const clarificationCount = countClientClarifications(fixture);
  const mappingRowCount = fixture.outputBundle.outputGraphs.reduce(
    (sum, graph) => sum + graph.mappingScheduleRows.length,
    0
  );
  const status = cockpitStatus(fixture.project.outputBundle.status);
  const projectTitle = projectTitleFromFixture(fixture);
  const partialScope = fixture.fixture.partialScope ?? null;
  const displayApartmentTypes = apartmentTypes(fixture);
  const core = ProjectCockpitViewSchema.parse({
    projectId: fixture.project.projectId,
    status,
    activeCatalogReleaseId: fixture.project.activeCatalogReleaseId,
    activeRuleSetId: fixture.project.activeRuleSetId,
    activeSpecSnapshotId: fixture.project.activeSpecSnapshotId,
    requirementCount: fixture.project.requirementIds.length,
    approvedRequirementCount: fixture.project.approvedRequirementIds.length,
    openRiskCount,
    openDecisionCount: fixture.openDecisions.length,
    needsUserDecisionCount,
    clientClarificationCount: clarificationCount,
    genuineRiskCount: openRiskCount,
    assumptionCount,
    clarificationCount,
    warningCount: fixture.warnings.length,
    exportReadiness: exportReadiness(fixture.project.outputBundle.status),
    updatedAt: fixture.fixture.generatedAt,
    scopeRows: rows,
    inputRows: fixture.inputs,
    apartmentTypes: apartmentTypesForSchema(displayApartmentTypes),
    dataGaps: partialScope ? [partialScope.message] : [],
    generatedFrom: fixture.fixture.generatedFrom
  });

  return {
    ...core,
    projectLabel: projectTitle,
    title: projectTitle,
    description: `${projectTitle} run through the project overview and comparison matrix.`,
    sourceInput: fixture.fixture.sourceInput,
    pipelineInputMode: fixture.fixture.pipelineInputMode,
    partialScope,
    approvedRequirementCount: core.approvedRequirementCount ?? 0,
    openDecisionCount: core.openDecisionCount ?? 0,
    needsUserDecisionCount: core.needsUserDecisionCount ?? 0,
    clientClarificationCount: core.clientClarificationCount ?? 0,
    genuineRiskCount: core.genuineRiskCount ?? 0,
    assumptionCount: core.assumptionCount ?? 0,
    clarificationCount: core.clarificationCount ?? 0,
    warningCount: core.warningCount ?? 0,
    mappingRowCount,
    scopeRows: core.scopeRows ?? [],
    inputRows: core.inputRows ?? [],
    apartmentTypes: displayApartmentTypes,
    dataGaps: core.dataGaps ?? [],
    scopeCounts: counts,
    attention: buildAttention({
      openRiskCount,
      needsUserDecisionCount,
      assumptionCount,
      clarificationCount
    }),
    steps: buildSteps(
      status,
      core.approvedRequirementCount ?? 0,
      needsUserDecisionCount,
      openRiskCount,
      mappingRowCount
    )
  };
};
